"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MIN_BILLABLE_MILESTONE_AMOUNT = void 0;
exports.paymentTermsToMilestones = paymentTermsToMilestones;
exports.resolveExtractedProjectMilestones = resolveExtractedProjectMilestones;
exports.filterBillableExtractedMilestones = filterBillableExtractedMilestones;
exports.reconcileMilestoneBudgetsWithSubMilestones = reconcileMilestoneBudgetsWithSubMilestones;
exports.checkMilestoneTotalAgainstContractValue = checkMilestoneTotalAgainstContractValue;
exports.applyDefaultMilestoneExtractionDates = applyDefaultMilestoneExtractionDates;
const allocate_payment_term_amounts_util_js_1 = require("../nucleus/allocate-payment-term-amounts.util.js");
/** Mirrors the projects API minimum milestone budget so the FE never posts rows the server rejects. */
exports.MIN_BILLABLE_MILESTONE_AMOUNT = 2;
/** Singapore GST, matching the 9% rate used across invoicing. */
const GST_RATE = 0.09;
/** Rounding noise and cent-level differences in the source document are not worth flagging. */
const CONTRACT_RECONCILIATION_TOLERANCE = 1;
function isValidPaymentTerm(term) {
    const amount = Number(term.budgetAmount);
    const percent = Number(term.budgetPercent);
    return ((Number.isFinite(amount) && amount > 0) ||
        (Number.isFinite(percent) && percent > 0));
}
function toIsoDate(value) {
    if (value == null)
        return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}
/** When AI returns payment schedule in `paymentTerms` but empty `milestones`, map terms to milestone rows. */
function paymentTermsToMilestones(paymentTerms, contractValue, usePercentageMode, includesGst) {
    const validTerms = paymentTerms.filter(isValidPaymentTerm);
    if (validTerms.length === 0)
        return [];
    // Amount-mode milestone budgets are pre-tax when GST is a separate add-on. Percentage mode keeps
    // the GST-inclusive contractValue so budgetPercent of the grand total (and later amount/contract
    // recomputes) stay consistent with what the client pays.
    const allocationBasis = !usePercentageMode && includesGst === true && contractValue > 0
        ? contractValue / (1 + GST_RATE)
        : contractValue;
    const contractTotal = allocationBasis > 0 ? Math.round(allocationBasis * 100) / 100 : 0;
    const allocated = contractTotal > 0
        ? (0, allocate_payment_term_amounts_util_js_1.allocatePaymentTermAmounts)(validTerms, contractTotal)
        : validTerms.map((term) => Number(term.budgetAmount) || 0);
    return validTerms.map((term, index) => {
        const budgetPercent = Number(term.budgetPercent);
        const pct = Number.isFinite(budgetPercent) && budgetPercent > 0 ? budgetPercent : null;
        const dueDate = toIsoDate(term.dueDate);
        const label = String(term.name ?? term.description ?? `Payment ${index + 1}`).trim();
        return {
            name: label || `Payment ${index + 1}`,
            description: term.description ?? null,
            budgetAmount: allocated[index] ?? (Number(term.budgetAmount) || 0),
            startDate: dueDate,
            endDate: dueDate,
            ...(usePercentageMode && pct != null
                ? { metadata: { budgetPercent: pct, usePercentageMode: true } }
                : {}),
        };
    });
}
/** Prefer explicit milestones; fall back to payment terms when the model split them per amount-mode prompt. */
function resolveExtractedProjectMilestones(input) {
    const milestones = input.milestones ?? [];
    if (milestones.length > 0) {
        return milestones;
    }
    if (input.skipPaymentTermsFallback === true) {
        return [];
    }
    const paymentTerms = input.paymentTerms ?? [];
    if (paymentTerms.length === 0) {
        return [];
    }
    return paymentTermsToMilestones(paymentTerms, Number(input.contractValue) || 0, input.usePercentageMode === true, input.includesGst);
}
/**
 * Contracts list non-billable rows ("NIL", "billed by contractor", inclusive items) at zero.
 * They fail the API's minimum-budget rule, so drop them and report the names for the user.
 */
function filterBillableExtractedMilestones(milestones) {
    const billable = [];
    const skippedNames = [];
    for (const milestone of milestones ?? []) {
        const amount = Number(milestone.budgetAmount);
        const percent = Number(milestone.metadata?.budgetPercent);
        const hasAmount = Number.isFinite(amount) && amount >= exports.MIN_BILLABLE_MILESTONE_AMOUNT;
        const hasPercent = Number.isFinite(percent) && percent > 0;
        if (hasAmount || hasPercent) {
            billable.push(milestone);
        }
        else {
            skippedNames.push(String(milestone.name ?? "").trim() || "Untitled milestone");
        }
    }
    return { milestones: billable, skippedNames };
}
/**
 * Section headings ("Carpentry Works") often have no printed subtotal, so the model estimates one while
 * transcribing the line items underneath verbatim. The estimate lands under the true sum and the API
 * rejects the milestone. The printed line items are the trustworthy figures, so lift the parent to match them.
 *
 * Only ever raises the parent: a sub-sum below the parent usually means the model listed a subset of the
 * section's lines, and lowering the parent there would silently drop money from the project total.
 */
function reconcileMilestoneBudgetsWithSubMilestones(milestones) {
    const correctedNames = [];
    const reconciled = (milestones ?? []).map((milestone) => {
        const subs = Array.isArray(milestone.subMilestones) ? milestone.subMilestones : [];
        if (subs.length === 0)
            return milestone;
        let subTotal = 0;
        let hasSubAmount = false;
        for (const sub of subs) {
            const amount = Number(sub?.budgetAmount);
            if (!Number.isFinite(amount))
                continue;
            subTotal += amount;
            hasSubAmount = true;
        }
        if (!hasSubAmount)
            return milestone;
        const rounded = Math.round(subTotal * 100) / 100;
        const parent = Number(milestone.budgetAmount);
        if (Number.isFinite(parent) && parent >= rounded)
            return milestone;
        correctedNames.push(String(milestone.name ?? "").trim() || "Untitled milestone");
        return { ...milestone, budgetAmount: rounded };
    });
    return { milestones: reconciled, correctedNames };
}
/**
 * The contract total is extracted independently of the line items, so it is a free cross-check on the
 * breakdown. A mismatch usually means the model double-counted a line into a sub-milestone or missed one
 * entirely — worth surfacing for review, since neither the amounts nor the total can be trusted blindly.
 */
function checkMilestoneTotalAgainstContractValue(input) {
    const contractValue = Number(input.contractValue);
    if (!Number.isFinite(contractValue) || contractValue <= 0)
        return null;
    if (!input.milestones?.length)
        return null;
    const milestoneTotal = input.milestones.reduce((total, milestone) => {
        const amount = Number(milestone.budgetAmount);
        return Number.isFinite(amount) ? total + amount : total;
    }, 0);
    // Line amounts are pre-tax whenever the document shows GST as a separate add-on.
    const expectedTotal = input.includesGst === true ? contractValue / (1 + GST_RATE) : contractValue;
    const difference = Math.round((milestoneTotal - expectedTotal) * 100) / 100;
    return {
        matches: Math.abs(difference) <= CONTRACT_RECONCILIATION_TOLERANCE,
        milestoneTotal: Math.round(milestoneTotal * 100) / 100,
        expectedTotal: Math.round(expectedTotal * 100) / 100,
        difference,
    };
}
/** Quotations rarely include milestone dates — default to project dates so create-flow upsert does not drop rows. */
function applyDefaultMilestoneExtractionDates(input) {
    const fallbackStart = toIsoDate(input.projectStartDate) ??
        toIsoDate(input.projectEndDate) ??
        new Date().toISOString();
    const fallbackEnd = toIsoDate(input.projectEndDate) ?? toIsoDate(input.projectStartDate) ?? fallbackStart;
    let inferredDateCount = 0;
    const milestones = (input.milestones ?? []).map((milestone) => {
        const startDate = toIsoDate(milestone.startDate) ?? fallbackStart;
        const endDate = toIsoDate(milestone.endDate) ?? fallbackEnd;
        if (!toIsoDate(milestone.startDate) || !toIsoDate(milestone.endDate)) {
            inferredDateCount++;
        }
        return {
            ...milestone,
            startDate,
            endDate,
        };
    });
    return { milestones, inferredDateCount };
}
